import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { authorisePayment, inr, fmtDate, quote } from "@/lib/subscription";
import { PlanGrid, CycleToggle, usePlans } from "@/components/PlanCards";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CreditCard, ArrowUpRight } from "lucide-react";

function UpgradeDialog({ open, onOpenChange, current, onDone }) {
  const data = usePlans();
  const { business } = useAuth();
  const [cycle, setCycle] = useState(current?.cycle || "yearly");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/subscription/upgrade", { plan_id: pending.plan.id, cycle, seats: pending.seats });
      if (r.needs_payment) await authorisePayment({ gateway: "razorpay", razorpay_key_id: r.razorpay_key_id, subscription: r.subscription }, business);
      toast.success(`Switched to ${pending.plan.name}`); setPending(null); onOpenChange(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Upgrade failed"); } finally { setBusy(false); }
  };
  const q = pending && quote(pending.plan, cycle, pending.seats);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto" data-testid="upgrade-dialog">
        <DialogHeader><DialogTitle>Upgrade your plan</DialogTitle></DialogHeader>
        <div className="w-fit"><CycleToggle cycle={cycle} setCycle={setCycle} /></div>
        {data && <PlanGrid plans={data.plans} cycle={cycle} currentId={cycle === current?.cycle ? current?.plan_id : null} onPick={(plan, seats) => setPending({ plan, seats })} />}
        <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
          <AlertDialogContent data-testid="upgrade-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Switch to {pending?.plan.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                {q && <>{q.seats} employees · {inr(pending.plan.monthly)} per employee/month. New total <b>{inr(q.amount)}</b> per {cycle === "yearly" ? "year" : "month"}, billed from your next billing date. Nothing is charged until you confirm.</>}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="upgrade-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); confirm(); }} className="bg-[#0D5C3A] hover:bg-[#084229]" data-testid="upgrade-confirm-btn">Confirm upgrade</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}

export function SubscriptionCard() {
  const { user, refresh } = useAuth();
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/subscription/status").then((r) => setS(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const sub = s?.subscription;
  if (!sub || user?.role !== "owner") return null;
  const full = s.seats_remaining === 0;
  const pct = Math.min(100, Math.round((s.seats_used / sub.seats) * 100));
  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid="subscription-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-400"><CreditCard className="h-4 w-4" /> Subscription</p>
          <p className="mt-1 font-heading text-xl font-bold text-stone-900" data-testid="subscription-plan-name">{sub.plan_name} <span className="text-sm font-medium capitalize text-stone-500">· {sub.cycle} · {sub.status === "trialing" ? "Free trial" : sub.status.replace("_", " ")}</span></p>
          <p className="text-sm text-stone-500">{inr(sub.monthly_per_seat)} per employee/month · {inr(sub.amount)} per {sub.cycle === "yearly" ? "year" : "month"}</p>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} className={full ? "bg-[#C85A32] hover:bg-[#a8481f]" : "bg-[#0D5C3A] hover:bg-[#084229]"} data-testid="subscription-upgrade-btn"><ArrowUpRight className="mr-1 h-4 w-4" /> Upgrade</Button>
      </div>
      <div className="mt-4">
        <div className="flex justify-between text-sm"><span data-testid="subscription-seats-used">{s.seats_used} of {sub.seats} seats used</span><span className={full ? "font-semibold text-[#C85A32]" : "text-stone-500"} data-testid="subscription-seats-remaining">{s.seats_remaining} left</span></div>
        <div className="mt-1.5 h-2 rounded-full bg-[#F0ECE1]"><div className={`h-2 rounded-full ${full ? "bg-[#C85A32]" : "bg-[#0D5C3A]"}`} style={{ width: `${pct}%` }} /></div>
        {full && <p className="mt-2 text-xs text-[#C85A32]" data-testid="subscription-limit-reached">You've reached your employee limit. Upgrade to add more people.</p>}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3 border-t border-[#F0ECE1] pt-4 text-xs text-stone-500">
        <div><p>Trial started</p><p className="font-semibold text-stone-800" data-testid="subscription-trial-start">{fmtDate(sub.trial_start)}</p></div>
        <div><p>Trial ends</p><p className="font-semibold text-stone-800" data-testid="subscription-trial-end">{fmtDate(sub.trial_end)}</p></div>
        <div><p>Next billing</p><p className="font-semibold text-stone-800" data-testid="subscription-next-billing">{fmtDate(sub.next_billing)}</p></div>
      </div>
      {open && <UpgradeDialog open={open} onOpenChange={setOpen} current={sub} onDone={() => { load(); refresh(); }} />}
    </div>
  );
}
